import { Box } from "@mui/material";
import type { IHeaderParams } from "ag-grid-community";
import { ReactNode, useEffect, useState } from "react";

export type AnalyticsSortableHeaderLabelRender = (
  params: IHeaderParams,
) => ReactNode;

export type AnalyticsHeaderAlign = "left" | "center";

interface AnalyticsSortableHeaderProps extends IHeaderParams {
  renderLabel?: AnalyticsSortableHeaderLabelRender;
  align?: AnalyticsHeaderAlign;
}

function AnalyticsSortableHeader({
  renderLabel,
  enableSorting,
  progressSort,
  column,
  displayName,
  align = "center",
}: AnalyticsSortableHeaderProps) {
  const [sort, setSort] = useState(column.getSort());

  useEffect(() => {
    const onSortChanged = () => setSort(column.getSort());
    column.addEventListener("sortChanged", onSortChanged);
    return () => column.removeEventListener("sortChanged", onSortChanged);
  }, [column]);

  const handleClick = (event: React.MouseEvent) => {
    if (!enableSorting) {
      return;
    }

    progressSort(event.shiftKey);
  };

  return (
    <Box
      className="ag-header-cell-label"
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: align === "left" ? "flex-start" : "center",
        textAlign: align,
        gap: 0.5,
        height: "100%",
        width: "100%",
        px: 0.5,
        cursor: enableSorting ? "pointer" : "default",
        userSelect: "none",
        color: "text.secondary",
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      <Box component="span" className="ag-header-cell-text" sx={{ display: "inline" }}>
        {renderLabel
          ? renderLabel({ column, displayName } as IHeaderParams)
          : displayName}
      </Box>
      {sort === "asc" ? (
        <span className="ag-icon ag-icon-asc" aria-hidden="true" />
      ) : sort === "desc" ? (
        <span className="ag-icon ag-icon-desc" aria-hidden="true" />
      ) : null}
    </Box>
  );
}

export function createAnalyticsSortableHeader(
  renderLabel: AnalyticsSortableHeaderLabelRender,
  align: AnalyticsHeaderAlign = "center",
) {
  return function SortableHeader(props: IHeaderParams) {
    return (
      <AnalyticsSortableHeader
        {...props}
        renderLabel={renderLabel}
        align={align}
      />
    );
  };
}

export default AnalyticsSortableHeader;
